/* eslint-disable indent */
import Vue from 'vue';
import permitService from '@/api/permit-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';
import clone from 'just-clone';
import impeccableReputationChecksService from '@/api/impeccable-reputation-checks-service';
import { getLocalStorageItem, setLocalStorageItem } from '@/util/local-storage';
import trainingResultService from '@/api/training-result-service';
import { helpers } from '@/util/helpers';

const snackbarMessageByResponseStatus = (statusCode, messageIdentifier) => {
  let snackbarMessage = '';
  if (statusCode === 400 || statusCode === 401 || statusCode === 403) {
    snackbarMessage = i18n.t('access_restricted.');
  } else if (statusCode === 404) {
    snackbarMessage = `${i18n.t('permit')} ${messageIdentifier} ${i18n.t('code_not_found')}.`;
  } else if (statusCode === 422) {
    snackbarMessage = i18n.t('validation_error');
  } else if (statusCode === 500) {
    snackbarMessage = i18n.t('server_error');
  } else {
    snackbarMessage = `${i18n.t('error')}: ${messageIdentifier}.`;
  }
  return snackbarMessage;
};

export const getDefaultAirport = (isMainAirport = true) => ({
  is_main_airport: isMainAirport,
});

export const getDefaultPermitApplicantUser = () => ({
  person: {
    nationality: 'lt',
  },
});

export const getDefaultPermitWorkplaces = () => ({
  until_now: false,
  worked_abroad: false,
});

export const getDefaultPermitLivingAddresses = () => ({
  until_now: false,
  city: '',
});

export const getDefaultPermitFormItem = () => ({
  applicant_user_type: 'existing',
  airports: [getDefaultAirport()],
  zones: [],
  documents: [],
  new_documents: [],
  permit_fields: [],
  status: '',
  is_visible: true,
  work_places: [getDefaultPermitWorkplaces()],
  living_addresses: [getDefaultPermitLivingAddresses()],
  applicant_user: getDefaultPermitApplicantUser(),
  is_internal_vehicle: false,
  is_paid: false,
  is_new_user: false,
  has_passed_technical_inspection: false,
  vehicle_in_good_technical_condition: false,
  accompanying_user_ids: [],
  has_foreign_country_living_permits: false,
  foreign_country_living_permits: [],
  has_previous_aviation_safety_training: false,
  should_notify_about_issuing: false,
  is_trailer_numbers_required: false,
  trailer_numbers: [],
});

export const getDefaultPermitFilterParams = () => ({});

export const getDefaultCheckedPermit = () => ({
  applicant_user: {
    person: {},
  },
});

const state = {
  permits: null,
  needActionPermits: null,
  permitPagination: {},
  editedPermit: {},
  permitValidationErrors: {},
  permitFilterParams: getDefaultPermitFilterParams(),
  newPermit: getDefaultPermitFormItem(),
  checkedPermit: getDefaultCheckedPermit(),
  permitReport: null,
  permitReportFilterParams: {},
  selectedAirport: null,
  selectedZone: null,
  selectedPost: null,
  securityCode: null,
  trainingDates: [],
  cleanCriminalRecordsValidationErrors: null,
};

const getters = {
  permitTypesMap() {
    return {
      temporary_person: {
        label: i18n.t('temporary_person'),
        value: 'temporary_person',
        color: '#BBDEFB',
        icon: 'mdi-account-clock',
      },
      permanent_person: {
        label: i18n.t('permanent_person'),
        value: 'permanent_person',
        color: '#42A5F5',
        icon: 'mdi-account',
      },
      temporary_vehicle: {
        label: i18n.t('temporary_vehicle'),
        value: 'temporary_vehicle',
        color: '#CFD8DC',
        icon: 'mdi-bus-clock',
      },
      permanent_vehicle: {
        label: i18n.t('permanent_vehicle'),
        value: 'permanent_vehicle',
        color: '#546E7A',
        icon: 'mdi-bus',
      },
    };
  },

  permitTypes(state, getters) {
    return Object.values(getters.permitTypesMap);
  },

  permitIssuedStatuses() {
    return [
      {
        label: i18n.t('permit_issued_statuses.valid'),
        value: 'valid',
      },
      {
        label: i18n.t('permit_issued_statuses.not_valid'),
        value: 'not_valid',
      },
      {
        label: i18n.t('permit_issued_statuses.temporary_suspended'),
        value: 'temporary_suspended',
      },
    ];
  },

  trainingStatuses() {
    return [
      {
        label: i18n.t('safety_training_statuses.not_started'),
        value: 'not_started',
      },
      {
        label: i18n.t('safety_training_statuses.exam_needs_passed'),
        value: 'exam_needs_passed',
      },
      {
        label: i18n.t('safety_training_statuses.exam_passed'),
        value: 'exam_passed',
      },
    ];
  },

  permitMainStatusMap() {
    return {
      not_submitted_or_editing: {
        label: i18n.t('permit_statuses.not_submitted_or_editing'),
        value: 'not_submitted_or_editing',
        color: 'red accent-1',
      },
      request_submitted: {
        label: i18n.t('permit_statuses.request_submitted'),
        value: 'request_submitted',
        color: 'yellow accent-1',
      },
      clarification_needed: {
        label: i18n.t('permit_statuses.clarification_needed'),
        value: 'clarification_needed',
        color: 'red accent-1',
      },
      checking_in_progress: {
        label: i18n.t('permit_statuses.checking_in_progress'),
        value: 'checking_in_progress',
        color: 'yellow accent-1',
      },
      request_approved: {
        label: i18n.t('permit_statuses.request_approved'),
        value: 'request_approved',
        color: 'green accent-1',
      },
      permit_issued: {
        label: i18n.t('permit_statuses.permit_issued'),
        value: 'permit_issued',
        color: 'green accent-1',
      },
      rejected: {
        label: i18n.t('permit_statuses.rejected'),
        value: 'rejected',
        color: 'red accent-1',
      },
      // checking_impeccable_reputation: {
      //   label: i18n.t('permit_statuses.checking_impeccable_reputation'),
      //   value: 'checking_impeccable_reputation',
      //   color: 'yellow accent-1',
      // },
      // awaiting_aviation_safety_training: {
      //   label: i18n.t('permit_statuses.awaiting_aviation_safety_training'),
      //   value: 'awaiting_aviation_safety_training',
      //   color: 'yellow accent-1',
    };
  },

  mainStatuses(state, getters) {
    return Object.values(getters.permitMainStatusMap);
  },

  permitRepetitionTypesMap() {
    return {
      new: {
        text: i18n.t('permit_repetition_types.new'),
        value: 'new',
        icon: 'mdi-alpha-n-box-outline',
      },
      extended: {
        text: i18n.t('permit_repetition_types.extended'),
        value: 'extended',
        icon: 'mdi-alpha-p-box-outline',
      },
      duplicate: {
        text: i18n.t('permit_repetition_types.duplicate'),
        value: 'duplicate',
        icon: 'mdi-alpha-d-box-outline',
      },
    };
  },

  permitRepetitionTypes(state, getters) {
    return Object.values(getters.permitRepetitionTypesMap);
  },

  securityCode(state) {
    return state.securityCode;
  },
};

const mutations = {
  SET_PERMITS(state, { data, meta }) {
    state.permits = data;
    state.permitPagination = {
      current_page: meta.current_page,
      per_page: meta.per_page,
      total: meta.total,
    };
  },

  SET_NEW_PERMIT(state, permit) {
    state.newPermit = permit;
  },

  SET_NEED_ACTION_PERMITS(state, { data }) {
    state.needActionPermits = data;
  },

  SET_OLDER_NEED_ACTION_PERMITS(state, { data }) {
    state.needActionPermits.push(...data);
  },

  SET_PERMIT_FILTER_PARAMS(state, params) {
    state.permitFilterParams = params;
  },

  SET_PERMIT_REPORT_FILTER_PARAMS(state, params) {
    state.permitReportFilterParams = params;
  },

  SET_EDITED_PERMIT(state, permit) {
    state.permitValidationErrors = {};
    state.editedPermit = JSON.parse(JSON.stringify({ ...permit, new_documents: [] }));
    state.editedPermit.accompanying_user_ids =
      permit.accompanying_users?.map((user) => user.id) || [];
    if (permit.type === 'permanent_person' && !permit.foreign_country_living_permits) {
      state.editedPermit.foreign_country_living_permits = [];
    }
  },

  STORE_PERMIT(state, permit) {
    state.permits.unshift(permit);
    state.permitPagination.total += 1;
    state.permitValidationErrors = {};
    state.newPermit = getDefaultPermitFormItem();
  },

  UPDATE_PERMIT(state, permit) {
    state.permits = updateArrayItem(state.permits, permit);
  },

  UPDATE_NEED_ACTION_PERMIT(state, permit) {
    state.needActionPermits = updateArrayItem(state.needActionPermits, permit);
  },

  DELETE_PERMIT(state, permit) {
    state.permits = removeArrayItem(state.permits, permit);
    state.permitPagination.total -= 1;
  },

  SET_PERMIT_VALIDATION_ERRORS(state, permitValidationErrors) {
    state.permitValidationErrors = permitValidationErrors;
  },

  RESET_PERMIT_VALIDATION_ERRORS(state) {
    state.permitValidationErrors = {};
  },

  SET_CHECKED_PERMIT(state, permit) {
    state.checkedPermit = permit;
  },

  SET_PERMIT_REPORT(state, permitReport) {
    state.permitReport = permitReport;
  },

  CLEAR_PERMIT_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.permitValidationErrors, field);
  },

  SET_ALL_AIRPORT_INFO(state, airportInfo) {
    state.selectedAirport = airportInfo.selectedAirport;
    state.selectedZone = airportInfo.selectedZone;
    state.selectedPost = airportInfo.selectedPost;
    state.securityCode = airportInfo.securityCode;
  },

  SET_SELECTED_AIRPORT(state, selectedAirport) {
    state.selectedAirport = selectedAirport;
  },

  SET_SELECTED_ZONE(state, selectedZone) {
    state.selectedZone = selectedZone;
  },

  SET_SELECTED_POST(state, selectedPost) {
    state.selectedPost = selectedPost;
  },

  SET_SECURITY_CODE(state, securityCode) {
    state.securityCode = securityCode;
  },

  UPDATE_BACKGROUND_CHECK(state, backgroundCheck) {
    for (let i = 0; i < state.permits.length; i++) {
      if (state.permits[i].id === backgroundCheck.permit_id) {
        state.permits[i].background_check = backgroundCheck;
      }
    }
  },

  SET_TRAINING_DATES(state, dates) {
    state.trainingDates = dates;
  },

  SET_CLEAN_CRIMINAL_RECORDS_VALIDATION_ERRORS(state, validationErrors) {
    state.cleanCriminalRecordsValidationErrors = validationErrors;
  },

  RESET_CLEAN_CRIMINAL_RECORDS_VALIDATION_ERRORS(state) {
    state.cleanCriminalRecordsValidationErrors = null;
  },
};

const actions = {
  fetchPermits({ commit }, params = {}) {
    commit('SET_PERMIT_FILTER_PARAMS', params);
    return permitService.getPage(params).then((res) => {
      commit('SET_PERMITS', res.data);
      if (params.page !== state.permitFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  fetchPermitReport({ commit }, params = {}) {
    commit('SET_PERMIT_REPORT_FILTER_PARAMS', params);
    return permitService.getPermitReport(params).then((res) => {
      commit('SET_PERMIT_REPORT', res.data);
      return res.data;
    });
  },

  fetchNeedActionPermits({ commit }, params = { need_action: true }) {
    return permitService.getPage(params).then((res) => {
      commit('SET_NEED_ACTION_PERMITS', res.data);
      return res.data;
    });
  },

  fetchOlderNeedActionPermits({ commit }, params = { need_action: true }) {
    return permitService.getPage(params).then((res) => {
      commit('SET_OLDER_NEED_ACTION_PERMITS', res.data);
      return res.data;
    });
  },

  storePermit({ commit }, permit) {
    if (permit.type === 'multiple_temporary_permit') {
      const newPermit = clone(permit);
      if (permit.names) {
        newPermit.names = permit.names.split('\n');
        for (let i = 0; i < newPermit.names.length; i++) {
          if (!newPermit.names[i]) {
            newPermit.names.splice(i, 1);
            i--;
          }
        }
      }
      return permitService
        .createMultiple(newPermit)
        .then((res) => {
          if (newPermit.status === 'permit_issued') {
            res.data.data.forEach((permit) => commit('STORE_PERMIT', permit));
            eventBus.$emit(OPEN_SNACKBAR, i18n.t('multiple_permit_created'));
          } else if (newPermit.status === 'checking_in_progress') {
            commit('STORE_PERMIT', res.data.data);
            eventBus.$emit(OPEN_SNACKBAR, i18n.t('multiple_permit_submitted'));
          }
          return res.data.data;
        })
        .catch((err) => {
          commit('SET_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
          throw err;
        });
    }

    return permitService
      .create(permit)
      .then((res) => {
        commit('STORE_PERMIT', res.data.data);
        eventBus.$emit(
          OPEN_SNACKBAR,
          i18n.t(
            res.data.data.type === 'temporary_person' &&
              res.data.data.status === 'checking_in_progress'
              ? 'permit_submitted'
              : 'permit_created',
          ),
        );
        return res.data.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  getValidations({ commit }, { permit, keysToCheck }) {
    return permitService.getValidation(permit).catch((err) => {
      const filteredErrors = {};
      const errors = mapErrorsToInputs(err);
      Object.keys(errors).forEach((field) => {
        if (keysToCheck.includes(field)) {
          filteredErrors[field] = errors[field];
        }
      });
      if (
        keysToCheck.includes('clean_criminal_record') &&
        filteredErrors['clean_criminal_record']
      ) {
        // we use different mutation for clean_criminal_record errors, so that we could keep
        // clean criminal records document file drop zone in permit form when the errors are reset
        commit(
          'SET_CLEAN_CRIMINAL_RECORDS_VALIDATION_ERRORS',
          filteredErrors.clean_criminal_record,
        );
        delete filteredErrors.clean_criminal_record;
      }
      commit('SET_PERMIT_VALIDATION_ERRORS', filteredErrors);
    });
  },

  checkPermit({ commit }, permitForCheck) {
    commit('SET_CHECKED_PERMIT', getDefaultCheckedPermit()); // clear old permit before checking
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('checking'));
    return permitService
      .checkPermit(permitForCheck)
      .then((res) => {
        eventBus.$emit(OPEN_SNACKBAR, { isOpen: false });
        commit('SET_CHECKED_PERMIT', res.data);
      })
      .catch((err) => {
        eventBus.$emit(
          OPEN_SNACKBAR,
          snackbarMessageByResponseStatus(err.response?.status, permitForCheck.unique_id),
        );
      });
  },

  editPermit({ state, commit }, permitId) {
    const permit = state.permits?.find((c) => c.id === permitId);
    if (permit) {
      commit('SET_EDITED_PERMIT', permit);
      return Promise.resolve(permit);
    }
    return permitService.getById(permitId).then((res) => {
      commit('SET_EDITED_PERMIT', res.data.data);
      return res.data.data;
    });
  },

  updatePermit({ state, commit, rootState }, {permit}) {
    const updatedPermit = clone(permit);
    if (updatedPermit.work_places.length === 0) {
      delete updatedPermit.work_places;
    }
    if (updatedPermit.living_addresses.length === 0) {
      delete updatedPermit.living_addresses;
    }
    return permitService
      .update(updatedPermit)
      .then((res) => {
        if (state.permits) {
          if (res.data.data.length > 1) {
            // When issuing mass permit, one permit turns into multiple permits,
            commit('DELETE_PERMIT', permit);
            res.data.data.forEach((permit) => commit('STORE_PERMIT', permit));
          } else if (
            res.data.data.type === 'temporary_person' &&
            (res.data.data.status === 'permit_issued' || res.data.data.status === 'rejected') &&
            ((helpers.$isSubjectAdmin() &&
              res.data.data.subject_id !== rootState.auth.currentUser.subject_id) ||
              (helpers.$isUser() &&
                res.data.data.applicant_user_id !== rootState.auth.currentUser.id))
          ) {
            // When subject admin changes temporary person permit of another subject status from
            // checking_in_progress  to permit_issued - permit should be removed from the list,
            // since subject admin can't see issued permits of other subjects
            // For user - their own permits should be updated, but others permits - deleted
            commit('DELETE_PERMIT', res.data.data);
          } else {
            commit('UPDATE_PERMIT', res.data.data);
          }
        }
        if (state.needActionPermits) {
          commit('UPDATE_NEED_ACTION_PERMIT', res.data.data);
        }
        if (updatedPermit.status === 'request_approved') {
          eventBus.$emit(OPEN_SNACKBAR, i18n.t('invitation_to_collect_permit_sent'));
        } else {
          eventBus.$emit(
            OPEN_SNACKBAR,
            i18n.t(res.data.data.length > 1 ? 'permits_created' : 'permit_updated'),
          );
        }
        return res.data.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  approvePermit({ commit }, permit) {
    return permitService
      .approve(permit)
      .then((res) => {
        commit('UPDATE_PERMIT', res.data.data);
        return res.data.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },
  uploadApplicantPhoto({ commit }, permit) {
    return permitService
      .applicantPhotoUpload(permit)
      .then((res) => {
        commit('UPDATE_PERMIT', res.data);
        commit('CLEAR_PERMIT_VALIDATION_ERRORS', 'applicant_photo');
        return res.data;
      })
    .catch((err) => {
      const mapped = mapErrorsToInputs(err);

      commit(
        'SET_PERMIT_VALIDATION_ERRORS',
        Object.keys(mapped).length
          ? mapped
          : { applicant_photo: [i18n.t('upload_failed')] }
      );

      throw err;
    });

  },

  uploadEncodedApplicantPhoto({ commit }, permit) {
    return permitService
      .applicantEncodedPhotoUpload(permit)
      .then((res) => {
        commit('UPDATE_PERMIT', res.data);
        commit('CLEAR_PERMIT_VALIDATION_ERRORS', 'applicant_photo');
        return res.data;
      })
      .catch((err) => {
        const mapped = mapErrorsToInputs(err);

        commit(
          'SET_PERMIT_VALIDATION_ERRORS',
          Object.keys(mapped).length
            ? mapped
            : { applicant_photo: [i18n.t('upload_failed')] }
        );

        throw err;
      });
  },

  clearPermitValidationErrors({ commit }, field) {
    commit('CLEAR_PERMIT_VALIDATION_ERRORS', field);
  },

  deletePermit({ commit }, permit) {
    openConfirmDialog({
      title: i18n.t('confirm_permit_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      permitService.delete(permit).then(() => {
        commit('DELETE_PERMIT', permit);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_deleted'));
      });
    });
  },

  setAirportInfo({ commit }, airportInfo) {
    commit('SET_ALL_AIRPORT_INFO', airportInfo);
    setLocalStorageItem('main_airport_id', airportInfo.selectedAirport);
    setLocalStorageItem('main_airport_zone', airportInfo.selectedZone);
    setLocalStorageItem('main_zone_post', airportInfo.selectedPost);
    setLocalStorageItem('security_code', airportInfo.securityCode);
  },

  getAirportInfo({ commit }) {
    const selectedAirport = getLocalStorageItem('main_airport_id');
    const selectedZone = getLocalStorageItem('main_airport_zone');
    const selectedZonePost = getLocalStorageItem('main_zone_post');
    const securityCode = getLocalStorageItem('security_code');

    if (selectedAirport) {
      commit('SET_SELECTED_AIRPORT', +selectedAirport);
    }
    if (selectedZone) {
      commit('SET_SELECTED_ZONE', +selectedZone);
    }
    if (selectedZonePost) {
      commit('SET_SELECTED_POST', selectedZonePost);
    }
    if (securityCode) {
      commit('SET_SECURITY_CODE', securityCode);
    }
  },

  async suspendVehiclePermits({ commit }, ids) {
    const confirmed = await openConfirmDialog({
      title: i18n.t(
        ids.length ? 'confirm_vehicle_permits_suspend' : 'confirm_continue_without_permit_suspend',
      ),
      confirmText: i18n.t(ids.length ? 'suspend_vehicle_permits' : 'continue_without_suspend'),
      maxWidth: ids.length ? 500 : 390,
    });
    if (!confirmed) {
      return false;
    }
    await permitService.suspendVehiclePermits(ids).catch((err) => {
      commit('SET_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
      throw err;
    });
    eventBus.$emit(OPEN_SNACKBAR, i18n.t(ids.length ? 'permits_suspended' : 'permits_confirmed'));
    return true;
  },

  updateIssuedPermitStatus({ commit }, payload) {
    return permitService
      .updateIssuedPermitStatus(payload)
      .then((res) => {
        commit('UPDATE_PERMIT', res.data.data);
        eventBus.$emit(
          OPEN_SNACKBAR,
          i18n.t(
            // eslint-disable-next-line no-nested-ternary
            payload.status === 'not_valid'
              ? 'permit_validity_stopped'
              : payload.status === 'valid'
              ? 'permit_validity_continued'
              : 'permit_temporary_suspended',
          ),
        );
        return res.data;
      })
      .catch((err) => {
        if (err.response?.data?.errors?.end_at?.length) {
          eventBus.$emit(OPEN_SNACKBAR, err.response.data.errors.end_at[0]);
        }
        throw err;
      });
  },

  updateZoning({ commit }, payload) {
    return permitService
      .updateZoning(payload)
      .then((res) => {
        commit('UPDATE_PERMIT', res.data.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_zoning_updated'));
        return res.data.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  fetchPermitScannerSettings({ commit }, securityCode) {
    commit('SET_SECURITY_CODE', securityCode);
    return permitService
      .getPermitScannerSettings(securityCode)
      .then((res) => {
        commit('zones/SET_ZONE_POSTS', res.data.permit_scanner_options.scanner_locations[0], {
          root: true,
        });
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  },

  updatePermitEndAt({ commit }, payload) {
    return permitService
      .updateEndAt(payload)
      .then((res) => {
        commit('UPDATE_PERMIT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('permit_end_at_updated'));
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  },

  updateBackgroundCheckStatus({ commit }, payload) {
    return impeccableReputationChecksService
      .updateStatus(payload)
      .then((res) => {
        commit('UPDATE_BACKGROUND_CHECK', res.data.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('background_check_updated'));
      })
      .catch((err) => {
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('background_check_confirm_failed'));
        throw err;
      });
  },

  getPermitTrainingResults({ commit }, user) {
    return trainingResultService.getPermanentPersonTrainingResults(user).then((res) => {
      commit('SET_TRAINING_DATES', res.data);
      return res.data;
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
